import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DESCRIPTION, DESCRIPTION_EDITOR_HEIGHT } from '../../../constants/inventories';
import { TinyMCEEditor } from '../../../types/shell';
import { toDescriptionEditorHtml } from '../../../utils/inventories';
import { wrapLinkDialogSubmit } from '../../../utils/linkHref';
// Direct import: the _mui barrel has a cycle that leaves its members undefined at load.
import { RichEditor } from '../../_mui/_components_/RichEditor/RichEditor';
import { InputLabel } from '../labels';

import styles from './InventoryItemForm.module.scss';

interface IProps {
  value?: string;
  onChange: (name: typeof DESCRIPTION, value: string) => void;
  required?: boolean;
  disabled?: boolean;
}

export const DescriptionEditor = ({ value, onChange, required, disabled }: IProps) => {
  // Uncontrolled editor: an external value (late fetch, Cancel) re-seeds and remounts it.
  // One object so html and key cannot desync; the version keys it, so typing never remounts.
  const [seed, setSeed] = useState({ html: value, version: 0 });
  // Null while TinyMCE boots.
  const editorRef = useRef<TinyMCEEditor | null>(null);

  const handleEditorInit = useCallback((editor: TinyMCEEditor) => {
    editorRef.current = editor;
    wrapLinkDialogSubmit(editor);
  }, []);

  useEffect(() => {
    if (value === undefined) {
      return;
    }

    // What is on screen: the editor's content, or the seed it was mounted with while still booting.
    const isAlreadyShown = editorRef.current
      ? [value, toDescriptionEditorHtml(value)].includes(editorRef.current.getContent())
      : seed.html === value;

    // Equal = the user's own echo; different = external change.
    if (isAlreadyShown) {
      return;
    }

    // Drop the outgoing instance so the next run cannot read content from a dead editor.
    editorRef.current = null;
    setSeed((prev) => ({ html: value, version: prev.version + 1 }));
  }, [value, seed.html]);

  const editorHtml = useMemo(() => toDescriptionEditorHtml(seed.html), [seed.html]);

  const handleInputChange = useCallback(
    (content: string) => {
      onChange(DESCRIPTION, content);
    },
    [onChange],
  );

  return (
    <div className={styles.descriptionEditorContainer}>
      <InputLabel value="Description" name={DESCRIPTION} required={required} />
      <RichEditor
        key={seed.version}
        value={editorHtml}
        isUncontrolled={true}
        height={DESCRIPTION_EDITOR_HEIGHT}
        onInputChange={handleInputChange}
        onEditorInit={handleEditorInit}
        dataAction="item_description"
        disabled={disabled}
        hasListItemAlignment={true}
        link_default_target="_blank"
        link_default_protocol="https"
        link_assume_external_targets={false}
        convert_urls={false}
        allow_unsafe_link_target={false}
      />
    </div>
  );
};
